<template>
    <div class="portal-page" :style="`direction:${lang.dir}`">
        <div class="portal-page__header" :class="{ 'portal-page__header--plain': loggedIn }">
            <template v-if="!loggedIn">
                <div class="portal-brand">
                    <i class="mdi mdi-car-side"></i>
                </div>
                <h1>{{ lang.customer_portal }}</h1>
                <p class="portal-page__subtitle">{{ lang.login_mobile_number }}</p>
            </template>
            <template v-else>
                <div class="portal-page__welcome">
                    <span class="portal-page__eyebrow">{{ lang.customer_portal }}</span>
                    <h1>{{ profile.full_name || lang.my_profile }}</h1>
                </div>
            </template>
        </div>

        <div class="portal-page__body">
            <!-- Step 1: enter mobile -->
            <div v-if="!loggedIn && step === 'mobile'" class="portal-card portal-block">
                <label><i class="mdi mdi-cellphone"></i> {{ lang.login_mobile_number }}</label>
                <b-form-input class="inborder portal-input" v-model="mobile" type="tel" :placeholder="lang.login_mobile_number" />
                <div v-if="message" class="portal-message" :class="messageType">{{ message }}</div>
                <b-button variant="primary" block class="mt-3" :disabled="busy" @click="sendLoginOtp">
                    {{ lang.send_verification_code }}
                </b-button>
            </div>

            <!-- Step 2: enter OTP -->
            <div v-else-if="!loggedIn && step === 'otp'" class="portal-card portal-block">
                <label><i class="mdi mdi-shield-check-outline"></i> {{ lang.verification_code }}</label>
                <b-form-input class="inborder portal-input portal-otp-input" v-model="otpCode" type="tel" maxlength="6" />
                <div v-if="message" class="portal-message" :class="messageType">{{ message }}</div>
                <b-button variant="primary" block class="mt-3" :disabled="busy" @click="verifyLoginOtp">
                    {{ lang.verify_code }}
                </b-button>
                <b-button variant="link" block :disabled="busy" @click="sendLoginOtp">
                    {{ lang.resend_code }}
                </b-button>
                <b-button variant="link" block @click="step = 'mobile'">
                    {{ lang.back }}
                </b-button>
            </div>

            <!-- Logged in -->
            <div v-else-if="loggedIn">
                <div class="portal-tabs">
                    <button class="portal-tab" :class="{active: activeTab==='profile'}" @click="activeTab='profile'">
                        <i class="mdi mdi-account-outline"></i>{{ lang.my_profile }}
                    </button>
                    <button class="portal-tab" :class="{active: activeTab==='records'}" @click="switchTab('records')">
                        <i class="mdi mdi-file-document-outline"></i>{{ lang.my_records }}
                    </button>
                    <button class="portal-tab" :class="{active: activeTab==='visit'}" @click="switchTab('visit')">
                        <i class="mdi mdi-calendar-clock-outline"></i>{{ lang.visit_request }}
                    </button>
                </div>

                <div class="portal-block">
                    <div v-if="message" class="portal-message" :class="messageType">{{ message }}</div>

                    <!-- Profile tab -->
                    <template v-if="activeTab === 'profile'">
                        <div class="portal-card">
                            <h2>{{ lang.my_profile }}</h2>
                            <label>{{ lang.full_name }}</label>
                            <b-form-input class="inborder portal-input" v-model="profile.full_name" />
                            <label>{{ lang.email }}</label>
                            <b-form-input class="inborder portal-input" v-model="profile.email" type="email" />
                            <b-button variant="primary" block class="mt-2" :disabled="busy" @click="saveProfile">
                                {{ lang.save }}
                            </b-button>
                        </div>

                        <div class="portal-card mt-3">
                            <h2>{{ lang.my_mobile_numbers }}</h2>
                            <div v-for="c in profile.contacts" :key="c.id" class="portal-contact-row">
                                <span><i class="mdi mdi-cellphone portal-row-icon"></i>{{ c.mobile }}</span>
                                <span v-if="c.is_main" class="portal-badge"><i class="mdi mdi-star"></i>{{ lang.main }}</span>
                                <b-button v-else size="sm" variant="outline-secondary" :disabled="busy" @click="promoteMobile(c.id)">
                                    {{ lang.make_main }}
                                </b-button>
                            </div>

                            <div v-if="addStep === 'idle'" class="mt-2">
                                <button class="portal-link-action" @click="addStep = 'mobile'">
                                    <i class="mdi mdi-plus-circle-outline"></i>{{ lang.add_new_mobile }}
                                </button>
                            </div>
                            <div v-else-if="addStep === 'mobile'" class="mt-2">
                                <label>{{ lang.login_mobile_number }}</label>
                                <b-form-input class="inborder portal-input" v-model="newMobile" type="tel" />
                                <b-button variant="primary" block class="mt-2" :disabled="busy" @click="sendAddMobileOtp">
                                    {{ lang.send_verification_code }}
                                </b-button>
                                <b-button variant="link" block @click="addStep = 'idle'">{{ lang.cancel }}</b-button>
                            </div>
                            <div v-else-if="addStep === 'otp'" class="mt-2">
                                <label>{{ lang.verification_code }}</label>
                                <b-form-input class="inborder portal-input portal-otp-input" v-model="newMobileCode" type="tel" maxlength="6" />
                                <b-button variant="primary" block class="mt-2" :disabled="busy" @click="verifyAddMobile">
                                    {{ lang.verify_code }}
                                </b-button>
                                <b-button variant="link" block @click="addStep = 'idle'">{{ lang.cancel }}</b-button>
                            </div>
                        </div>

                        <b-button variant="outline-danger" block class="mt-4" @click="logout">
                            <i class="mdi mdi-logout"></i> {{ lang.logout }}
                        </b-button>
                    </template>

                    <!-- Records tab (C2) -->
                    <template v-else-if="activeTab === 'records'">
                        <h2 class="portal-section-title"><i class="mdi mdi-car-wrench"></i>{{ lang.job_cards }}</h2>
                        <div v-if="records.job_cards.length === 0" class="portal-empty">{{ lang.no_records }}</div>
                        <div v-for="c in records.job_cards" :key="'card'+c.id" class="portal-record-card">
                            <div class="portal-record-card__row">
                                <strong>#{{ c.display_number }}</strong>
                                <span class="portal-amount">{{ $RoundNums(c.total,2) }}</span>
                            </div>
                            <div class="portal-record-card__meta">{{ c.car }} — {{ c.date }}</div>
                            <div class="portal-record-card__actions">
                                <b-button size="sm" variant="outline-primary" @click="openPdf('newcard', c.id)"><i class="mdi mdi-file-pdf-box"></i>{{ lang.download_pdf }}</b-button>
                                <b-button size="sm" variant="outline-secondary" disabled :title="lang.coming_soon">{{ lang.accept_request }}</b-button>
                            </div>
                        </div>

                        <h2 class="portal-section-title mt-4"><i class="mdi mdi-receipt-text-outline"></i>{{ lang.invoices }}</h2>
                        <div v-if="records.invoices.length === 0" class="portal-empty">{{ lang.no_records }}</div>
                        <div v-for="i in records.invoices" :key="'inv'+i.id" class="portal-record-card">
                            <div class="portal-record-card__row">
                                <strong>#{{ i.display_number }}</strong>
                                <span class="portal-amount">{{ i.total }}</span>
                            </div>
                            <div class="portal-record-card__meta">{{ lang.remaining }}: {{ i.remaining }} — {{ i.date }}</div>
                            <div class="portal-record-card__actions">
                                <b-button size="sm" variant="outline-primary" @click="openPdf('invid', i.id)"><i class="mdi mdi-file-pdf-box"></i>{{ lang.download_pdf }}</b-button>
                            </div>
                        </div>

                        <h2 class="portal-section-title mt-4"><i class="mdi mdi-file-document-edit-outline"></i>{{ lang.quotations }}</h2>
                        <div v-if="records.quotations.length === 0" class="portal-empty">{{ lang.no_records }}</div>
                        <div v-for="q in records.quotations" :key="'quot'+q.id" class="portal-record-card">
                            <div class="portal-record-card__row">
                                <strong>#{{ q.display_number }}</strong>
                                <span class="portal-amount">{{ q.total }}</span>
                            </div>
                            <div class="portal-record-card__meta">{{ q.date }}</div>
                            <div class="portal-record-card__actions">
                                <b-button size="sm" variant="outline-primary" @click="openPdf('quotid', q.id)"><i class="mdi mdi-file-pdf-box"></i>{{ lang.download_pdf }}</b-button>
                            </div>
                        </div>
                    </template>

                    <!-- Visit request tab (C3) -->
                    <template v-else-if="activeTab === 'visit'">
                        <div class="portal-card">
                            <h2>{{ lang.visit_request }}</h2>
                            <label>{{ lang.request_date }}</label>
                            <b-form-input class="inborder portal-input" v-model="visitForm.date" type="date" />
                            <label>{{ lang.request_time }}</label>
                            <b-form-input class="inborder portal-input" v-model="visitForm.time" type="time" />
                            <label>{{ lang.request_note }}</label>
                            <b-form-textarea class="inborder portal-input" v-model="visitForm.note" rows="3" />
                            <b-button variant="primary" block class="mt-2" :disabled="busy" @click="submitVisit">
                                {{ lang.submit_request }}
                            </b-button>
                        </div>

                        <h2 class="portal-section-title mt-4"><i class="mdi mdi-history"></i>{{ lang.my_requests }}</h2>
                        <div v-if="visitRequests.length === 0" class="portal-empty">{{ lang.no_records }}</div>
                        <div v-for="r in visitRequests" :key="r.id" class="portal-record-card">
                            <div class="portal-record-card__row">
                                <strong>{{ r.date }}<span v-if="r.time"> {{ r.time }}</span></strong>
                                <span class="portal-status" :class="'status-' + r.status">{{ requestStatusLabel(r.status) }}</span>
                            </div>
                            <div v-if="r.note" class="portal-record-card__meta">{{ r.note }}</div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'

const TOKEN_KEY = 'customer_portal_token';

export default {
    name: 'PortalCustomerLogin',
    data() {
        return {
            step: 'mobile',
            mobile: '',
            otpCode: '',
            token: '',
            busy: false,
            message: '',
            messageType: 'info',
            profile: { full_name: '', email: '', contacts: [] },
            addStep: 'idle',
            newMobile: '',
            newMobileCode: '',
            activeTab: 'profile',
            records: { job_cards: [], invoices: [], quotations: [] },
            recordsLoaded: false,
            visitForm: { date: '', time: '', note: '' },
            visitRequests: [],
            visitRequestsLoaded: false,
        }
    },
    computed: {
        loggedIn(){
            return !!this.token;
        },
        lang: function(){
            if(this.$cookies.get(this.$store.state.LangCooki) != null){
                if(this.$cookies.get(this.$store.state.LangCooki) == 'en'){
                    return this.$store.state.lang.en;
                }else if(this.$cookies.get(this.$store.state.LangCooki) == 'ur'){
                    return this.$store.state.lang.ur;
                }else{
                    return this.$store.state.lang.ar;
                }
            }
            else if(this.$store.state.deflang == 'en'){
                return this.$store.state.lang.en;
            }
            else if(this.$store.state.deflang == 'ur'){
                return this.$store.state.lang.ur;
            }else{
                return this.$store.state.lang.ar;
            }
        },
        isEn(){
            return this.lang.langname === 'en';
        },
        errorMessages(){
            const en = {
                mobile_required: 'Please enter your mobile number.',
                mobile_not_registered: 'This mobile number is not registered.',
                otp_sent: 'Verification code sent.',
                otp_recently_sent: 'A code was already sent recently, please wait a moment.',
                otp_not_found: 'No pending verification code found, please request a new one.',
                otp_expired: 'This code has expired, please request a new one.',
                too_many_attempts: 'Too many wrong attempts, please request a new code.',
                invalid_code: 'Incorrect code.',
                mobile_and_code_required: 'Please enter the code.',
                unauthorized: 'Your session has expired, please log in again.',
                nothing_to_update: 'Nothing to update.',
                updated: 'Saved.',
                mobile_already_yours: 'This number is already yours.',
                mobile_taken: 'This mobile number is already registered to another account.',
                mobile_added: 'Mobile number added.',
                contact_not_found: 'Number not found.',
                promoted: 'Main mobile updated.',
                date_required: 'Please choose a date.',
                request_submitted: 'Your request has been submitted.',
            };
            const ar = {
                mobile_required: 'الرجاء إدخال رقم الجوال.',
                mobile_not_registered: 'رقم الجوال غير مسجل.',
                otp_sent: 'تم إرسال رمز التحقق.',
                otp_recently_sent: 'تم إرسال رمز مؤخراً، الرجاء الانتظار قليلاً.',
                otp_not_found: 'لا يوجد رمز تحقق فعال، الرجاء طلب رمز جديد.',
                otp_expired: 'انتهت صلاحية الرمز، الرجاء طلب رمز جديد.',
                too_many_attempts: 'محاولات خاطئة كثيرة، الرجاء طلب رمز جديد.',
                invalid_code: 'رمز غير صحيح.',
                mobile_and_code_required: 'الرجاء إدخال الرمز.',
                unauthorized: 'انتهت الجلسة، الرجاء تسجيل الدخول من جديد.',
                nothing_to_update: 'لا يوجد تغييرات.',
                updated: 'تم الحفظ.',
                mobile_already_yours: 'هذا الرقم مسجل لديك بالفعل.',
                mobile_taken: 'هذا الرقم مسجل لحساب آخر.',
                mobile_added: 'تمت إضافة رقم الجوال.',
                contact_not_found: 'الرقم غير موجود.',
                promoted: 'تم تحديث الرقم الرئيسي.',
                date_required: 'الرجاء اختيار تاريخ.',
                request_submitted: 'تم إرسال طلبك بنجاح.',
            };
            return this.isEn ? en : ar;
        },
    },
    methods: {
        describe(code){
            return this.errorMessages[code] || code;
        },
        post(type, extra = {}){
            const data = new FormData();
            data.append('type', type);
            Object.keys(extra).forEach((k) => data.append(`data[${k}]`, extra[k]));
            return axios.post(this.$store.state.SAMCOTEC.r_path, data).then((res) => res.data);
        },
        showMessage(res, successType = 'success'){
            const code = res && res.error ? res.error.message : '';
            this.message = this.describe(code);
            this.messageType = (res && res.error && res.error.number === 200) ? successType : 'error';
        },
        sendLoginOtp(){
            if(!this.mobile){ this.message = this.describe('mobile_required'); this.messageType = 'error'; return; }
            this.busy = true;
            this.post('requestCustomerOtp', { mobile: this.mobile }).then((res) => {
                this.busy = false;
                this.showMessage(res);
                if(res.error && res.error.number === 200){
                    this.step = 'otp';
                }
            }).catch(() => { this.busy = false; });
        },
        verifyLoginOtp(){
            this.busy = true;
            this.post('verifyCustomerOtp', { mobile: this.mobile, otp_code: this.otpCode }).then((res) => {
                this.busy = false;
                if(res.error && res.error.number === 200){
                    this.token = res.results.token;
                    localStorage.setItem(TOKEN_KEY, this.token);
                    this.message = '';
                    this.loadProfile();
                }else{
                    this.showMessage(res);
                }
            }).catch(() => { this.busy = false; });
        },
        loadProfile(){
            this.post('getCustomerProfile', { token: this.token }).then((res) => {
                if(res.error && res.error.number === 200){
                    this.profile = res.results;
                }else{
                    this.showMessage(res);
                    if(res.error && res.error.number === 401){
                        this.doLogoutLocally();
                    }
                }
            });
        },
        saveProfile(){
            this.busy = true;
            this.post('updateCustomerProfile', { token: this.token, full_name: this.profile.full_name, email: this.profile.email }).then((res) => {
                this.busy = false;
                this.showMessage(res);
            }).catch(() => { this.busy = false; });
        },
        promoteMobile(contactId){
            this.busy = true;
            this.post('promoteMainMobile', { token: this.token, contact_id: contactId }).then((res) => {
                this.busy = false;
                this.showMessage(res);
                if(res.error && res.error.number === 200){
                    this.loadProfile();
                }
            }).catch(() => { this.busy = false; });
        },
        sendAddMobileOtp(){
            if(!this.newMobile){ this.message = this.describe('mobile_required'); this.messageType = 'error'; return; }
            this.busy = true;
            this.post('requestAddMobileOtp', { token: this.token, mobile: this.newMobile }).then((res) => {
                this.busy = false;
                this.showMessage(res);
                if(res.error && res.error.number === 200){
                    this.addStep = 'otp';
                }
            }).catch(() => { this.busy = false; });
        },
        verifyAddMobile(){
            this.busy = true;
            this.post('verifyAddMobileOtp', { token: this.token, mobile: this.newMobile, otp_code: this.newMobileCode }).then((res) => {
                this.busy = false;
                this.showMessage(res);
                if(res.error && res.error.number === 200){
                    this.addStep = 'idle';
                    this.newMobile = '';
                    this.newMobileCode = '';
                    this.loadProfile();
                }
            }).catch(() => { this.busy = false; });
        },
        switchTab(tab){
            this.activeTab = tab;
            if(tab === 'records' && !this.recordsLoaded){
                this.loadRecords();
            }
            if(tab === 'visit' && !this.visitRequestsLoaded){
                this.loadVisitRequests();
            }
        },
        loadRecords(){
            this.post('getCustomerRecords', { token: this.token }).then((res) => {
                if(res.error && res.error.number === 200){
                    this.records = res.results;
                    this.recordsLoaded = true;
                }else{
                    this.showMessage(res);
                }
            });
        },
        openPdf(param, id){
            window.open(`${window.location.origin}/api/print.php?${param}=${id}`, '_blank');
        },
        loadVisitRequests(){
            this.post('getMyVisitRequests', { token: this.token }).then((res) => {
                if(res.error && res.error.number === 200){
                    this.visitRequests = res.results;
                    this.visitRequestsLoaded = true;
                }else{
                    this.showMessage(res);
                }
            });
        },
        requestStatusLabel(status){
            const en = { pending: 'Pending', confirmed: 'Confirmed', rejected: 'Rejected' };
            const ar = { pending: 'قيد الانتظار', confirmed: 'تم التأكيد', rejected: 'مرفوض' };
            const map = this.isEn ? en : ar;
            return map[status] || status;
        },
        submitVisit(){
            this.busy = true;
            this.post('submitVisitRequest', { token: this.token, date: this.visitForm.date, time: this.visitForm.time, note: this.visitForm.note }).then((res) => {
                this.busy = false;
                this.showMessage(res);
                if(res.error && res.error.number === 200){
                    this.visitForm = { date: '', time: '', note: '' };
                    this.visitRequestsLoaded = false;
                    this.loadVisitRequests();
                }
            }).catch(() => { this.busy = false; });
        },
        logout(){
            this.post('logoutCustomer', { token: this.token }).finally(() => {
                this.doLogoutLocally();
            });
        },
        doLogoutLocally(){
            localStorage.removeItem(TOKEN_KEY);
            this.token = '';
            this.mobile = '';
            this.otpCode = '';
            this.step = 'mobile';
            this.message = '';
            this.activeTab = 'profile';
            this.records = { job_cards: [], invoices: [], quotations: [] };
            this.recordsLoaded = false;
            this.visitRequests = [];
            this.visitRequestsLoaded = false;
        },
    },
    created(){
        const saved = localStorage.getItem(TOKEN_KEY);
        if(saved){
            this.token = saved;
            this.loadProfile();
        }
    },
}
</script>

<style scoped>
.portal-page {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
}

/* ---- Header ---- */
.portal-page__header {
    padding: 40px 24px 28px;
    background: linear-gradient(160deg, var(--portal-ink) 0%, #1e3247 100%);
    color: #fff;
    text-align: center;
    border-radius: 0 0 28px 28px;
}
.portal-page__header--plain {
    text-align: start;
    padding: 28px 24px 20px;
    border-radius: 0 0 24px 24px;
}
.portal-brand {
    width: 56px;
    height: 56px;
    margin: 0 auto 14px;
    border-radius: 16px;
    background: var(--portal-accent);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 18px -6px rgba(221, 126, 31, .55);
}
.portal-brand .mdi {
    font-size: 30px;
    color: var(--portal-accent-ink);
}
.portal-page__header h1 {
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
}
.portal-page__subtitle {
    margin: 6px 0 0;
    font-size: .85rem;
    color: rgba(255, 255, 255, .68);
}
.portal-page__eyebrow {
    display: block;
    font-size: .7rem;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--portal-accent);
    font-weight: 700;
    margin-bottom: 4px;
}

/* ---- Body & generic blocks ---- */
.portal-page__body {
    flex: 1;
    padding: 20px 18px 28px;
}
.portal-card {
    background: var(--portal-surface);
    border: 1px solid var(--portal-border);
    border-radius: var(--portal-radius-lg);
    box-shadow: var(--portal-shadow);
    padding: 18px 16px;
}
.portal-block label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 14px;
    margin-bottom: 6px;
    font-size: .78rem;
    font-weight: 600;
    color: var(--portal-ink-muted);
    letter-spacing: .01em;
}
.portal-block label:first-child { margin-top: 0; }
.portal-block h2,
.portal-card h2 {
    font-size: 1.02rem;
    font-weight: 700;
    margin: 0 0 12px;
    color: var(--portal-ink);
}
.portal-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: .82rem;
    font-weight: 700;
    letter-spacing: .03em;
    text-transform: uppercase;
    color: var(--portal-ink-muted);
    margin: 0 0 10px 4px;
}
.portal-section-title .mdi { color: var(--portal-accent); font-size: 1.05rem; }

/* ---- Inputs ---- */
.portal-input {
    background: var(--portal-surface-2) !important;
    border: 1px solid var(--portal-border) !important;
    border-radius: var(--portal-radius-md) !important;
    padding: 10px 12px !important;
    color: var(--portal-ink) !important;
}
.portal-input:focus {
    border-color: var(--portal-accent) !important;
    box-shadow: 0 0 0 3px var(--portal-accent-soft) !important;
}
.portal-otp-input {
    text-align: center;
    font-size: 1.6rem;
    font-weight: 700;
    letter-spacing: .5em;
    text-indent: .5em;
}

/* ---- Buttons (override Bootstrap defaults to the amber accent) ---- */
.portal-block >>> .btn-primary,
.portal-card >>> .btn-primary {
    background: var(--portal-accent) !important;
    border-color: var(--portal-accent) !important;
    color: var(--portal-accent-ink) !important;
    font-weight: 700;
    border-radius: var(--portal-radius-md) !important;
    padding: 11px !important;
}
.portal-block >>> .btn-primary:hover,
.portal-card >>> .btn-primary:hover {
    background: var(--portal-accent-dark) !important;
    border-color: var(--portal-accent-dark) !important;
}
.portal-block >>> .btn-primary:disabled {
    opacity: .55;
}
.portal-block >>> .btn-outline-primary {
    color: var(--portal-accent-dark) !important;
    border-color: var(--portal-accent) !important;
    border-radius: var(--portal-radius-md) !important;
    font-weight: 600;
}
.portal-block >>> .btn-outline-primary:hover {
    background: var(--portal-accent-soft) !important;
}
.portal-block >>> .btn-outline-secondary {
    color: var(--portal-ink-muted) !important;
    border-color: var(--portal-border) !important;
    border-radius: var(--portal-radius-md) !important;
}
.portal-block >>> .btn-outline-danger {
    color: var(--portal-bad) !important;
    border-color: var(--portal-bad) !important;
    border-radius: var(--portal-radius-md) !important;
    font-weight: 600;
}
.portal-block >>> .btn-outline-danger:hover {
    background: var(--portal-bad-soft) !important;
}
.portal-block >>> .btn-link {
    color: var(--portal-ink-muted) !important;
    font-size: .85rem;
    text-decoration: none;
}
.portal-link-action {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    padding: 0;
    color: var(--portal-accent-dark);
    font-weight: 700;
    font-size: .88rem;
}

/* ---- Messages ---- */
.portal-message {
    display: flex;
    align-items: center;
    margin-top: 10px;
    padding: 10px 12px;
    border-radius: var(--portal-radius-md);
    font-size: .85rem;
    font-weight: 500;
}
.portal-message.error {
    background: var(--portal-bad-soft);
    color: var(--portal-bad);
}
.portal-message.success {
    background: var(--portal-good-soft);
    color: var(--portal-good);
}
.portal-message.info {
    background: var(--portal-accent-soft);
    color: var(--portal-accent-dark);
}

/* ---- Contacts ---- */
.portal-contact-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid var(--portal-border);
    font-size: .92rem;
}
.portal-contact-row:last-of-type { border-bottom: none; }
.portal-row-icon {
    color: var(--portal-ink-muted);
    margin-inline-end: 8px;
}
.portal-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: var(--portal-good-soft);
    color: var(--portal-good);
    font-size: .7rem;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: var(--portal-radius-pill);
}

/* ---- Tabs (segmented control) ---- */
.portal-tabs {
    display: flex;
    gap: 6px;
    margin: -8px 18px 18px;
    padding: 4px;
    background: var(--portal-surface-2);
    border: 1px solid var(--portal-border);
    border-radius: var(--portal-radius-pill);
}
.portal-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    background: none;
    border: none;
    padding: 9px 4px;
    font-size: .74rem;
    font-weight: 600;
    color: var(--portal-ink-muted);
    border-radius: var(--portal-radius-pill);
    white-space: nowrap;
}
.portal-tab .mdi { font-size: 1rem; }
.portal-tab.active {
    background: var(--portal-ink);
    color: #fff;
}

/* ---- Records / cards ---- */
.portal-empty {
    color: var(--portal-ink-muted);
    padding: 20px 0;
    text-align: center;
    font-size: .88rem;
}
.portal-record-card {
    background: var(--portal-surface);
    border: 1px solid var(--portal-border);
    border-radius: var(--portal-radius-lg);
    box-shadow: var(--portal-shadow);
    padding: 14px;
    margin-bottom: 12px;
}
.portal-record-card__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.portal-record-card__row strong {
    color: var(--portal-ink);
}
.portal-amount {
    font-weight: 700;
    color: var(--portal-accent-dark);
    font-variant-numeric: tabular-nums;
}
.portal-record-card__meta {
    font-size: .8rem;
    color: var(--portal-ink-muted);
    margin: 4px 0 10px;
}
.portal-record-card__actions {
    display: flex;
    gap: 8px;
}
.portal-status {
    font-size: .7rem;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: var(--portal-radius-pill);
    white-space: nowrap;
}
.status-pending { background: var(--portal-warn-soft); color: var(--portal-warn); }
.status-confirmed { background: var(--portal-good-soft); color: var(--portal-good); }
.status-rejected { background: var(--portal-bad-soft); color: var(--portal-bad); }
</style>
